import React, { ChangeEvent, CSSProperties, FC, useCallback, useMemo, useState } from 'react';
import {
  Link,
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom';
import {
  Button,
  Col,
  Container,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
} from 'reactstrap';
import { AuditTagType, auditTagTypes } from '../../../../shared/audit';
import {
  Breadcrumb,
  BreadcrumbItemProps,
  InlineHelp,
  MaterialIcon,
  RichMarkdownIt,
  UserStateComponent,
} from '../../../components';
import { DateT, DateTime } from '../../../components/Date';
import { LogEntry, LogResult } from '../../../models';
import { useTranslation } from '../../../translations';
import { Fetcher, useConfig, useConvention, useFetcher, useToggle } from '../../../utils';
import { LogDetailModal } from './LogDetailModal';
import { TimePickerModal } from './TimePicker';
import { makeRelativeLogLink, TableQueryOpts, urlSearchParamsToTableOpts } from './query';
import './style.scss';

export const AuditPage: FC = () => {
  return (
    <UserStateComponent>
      <Switch>
        <Route component={ListAuditEntities} exact path="/housekeeping/auditlog" />
        <Route component={AuditEntityRoute} exact path="/housekeeping/auditlog/:entityId" />
        <Route
          component={AuditObjectRoute}
          exact
          path="/housekeeping/auditlog/:entityId/:objectId"
        />
      </Switch>
    </UserStateComponent>
  );
};

const AuditEntityRoute: FC<RouteComponentProps<{ entityId: string }>> = ({ match }) => {
  const { entityId } = match.params;
  return <AuditObjectPage entityId={entityId} showBreadcrumbs />;
};

const AuditObjectRoute: FC<RouteComponentProps<{ entityId: string; objectId: string }>> = ({
  match,
}) => {
  const { entityId, objectId } = match.params;
  return <AuditObjectPage entityId={entityId} objectId={objectId} showBreadcrumbs />;
};

const ListAuditEntities: FC = () => {
  const { ts } = useTranslation();
  const fetcher = useFetcher(async () => await api.getAuditEntities());

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  return (
    <>
      <LogsBreadcrumbs />
      <div>{ts('select_a_pivot_to_browse')}</div>
      <ul>
        {fetcher.data.map((entity) => {
          return (
            <li key={entity.name}>
              <Link to={`/housekeeping/auditlog/${entity.name}`}>{entity.name}</Link>
            </li>
          );
        })}
      </ul>
    </>
  );
};

function handleFilter(
  params: TableQueryOpts,
  item: string,
  direction: 'include' | 'exclude',
  operation: 'add' | 'remove',
) {
  let excludeArr = params.exclude;
  let includeArr = params.include;

  if (operation === 'add') {
    if (direction === 'exclude') {
      includeArr = includeArr.filter((t: string) => t !== item);
      if (!excludeArr.includes(item)) {
        excludeArr = [...excludeArr, item];
      }
    } else {
      excludeArr = excludeArr.filter((t: string) => t !== item);
      if (!includeArr.includes(item)) {
        includeArr = [...includeArr, item];
      }
    }
  } else if (direction === 'exclude') {
    excludeArr = excludeArr.filter((t: string) => t !== item);
  } else {
    includeArr = includeArr.filter((t: string) => t !== item);
  }

  return makeRelativeLogLink(params, {
    include: includeArr,
    exclude: excludeArr,
  });
}

const TagsList: FC<{
  params: TableQueryOpts;
  tags: string[];
  direction: 'include' | 'exclude';
}> = ({ params, tags, direction }) => {
  return tags.map((tag) => {
    return (
      <div className="hstack filter-tag" key={tag} style={{ fontSize: '11px' }}>
        <div style={{ paddingTop: '2px' }}>
          <MaterialIcon
            name={direction === 'include' ? 'add' : 'remove'}
            small
            type={direction === 'include' ? 'primary' : 'danger'}
          />
        </div>
        <span className="ps-1">{tag}</span>
        <Button
          className={`tag-button ${tag} remove`}
          id={`${tag}.remove`}
          tag={Link}
          to={handleFilter(params, tag, direction, 'remove')}
        >
          <div className="align-content-center">
            <MaterialIcon className="my-auto" name="close" small />
          </div>
        </Button>
      </div>
    );
  });
};

const TypeFilter: FC<{ readonly params: TableQueryOpts }> = ({ params }) => {
  const type: string = params.type!;
  const { ts } = useTranslation();
  return (
    <Row className="type-filter-row shrink-item text-nowrap">
      <Col className="flex-grow-0">
        <span style={{ fontWeight: 'bold' }}>{ts('auditlogs.type.type_filter')}</span>
      </Col>
      <Col>
        <Row className="justify-content-center flex-grow-0">
          <Col className="flex-grow-0 p-0">{type}</Col>
          <Col className="flex-grow-0">
            <Link
              className="type-filter-remove"
              id="type-filter-remove"
              to={makeRelativeLogLink(params, { type: '' })}
            >
              {ts('auditlogs.type.remove')}
            </Link>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

interface LogDetailsProps {
  readonly entry: LogEntry;
  readonly params: TableQueryOpts;
}

const LogDetails: FC<LogDetailsProps> = ({ entry, params }) => {
  const [showRaw, setShowRaw] = useToggle(false);

  const tags = entry.tags.map((tag) => {
    return <LogTag key={entry.uniqueId} params={params} tag={tag} />;
  });

  const { ts } = useTranslation();

  return (
    <Col className="p-0 ms-4" style={{ fontSize: '11px' }}>
      <div className="hstack pb-1">
        <Col className="px-2 ps-0 flex-grow-0">
          <Button className="raw-button text-nowrap" onClick={setShowRaw}>
            <span>{ts(showRaw ? 'hide_raw' : 'show_raw')}</span>
          </Button>
        </Col>
        <Col className="flex-grow-0 text-nowrap">
          <Link
            className={`type-filter-add ${entry.type}`}
            to={makeRelativeLogLink(params, { type: entry.type })}
          >
            {entry.type}
          </Link>
        </Col>
        <Col className="flex-grow-0">
          <InlineHelp>
            <RichMarkdownIt source={ts('auditlogs.help.type_and_tags')} />
          </InlineHelp>
        </Col>
      </div>

      {showRaw && (
        <pre className="raw-preview shrink-item">{JSON.stringify(entry.meta, null, 2)}</pre>
      )}
      {tags.length > 0 && <div className="shrink-item hstack gap-1 my-1">{tags}</div>}
    </Col>
  );
};

const LogTag: FC<{
  readonly params: TableQueryOpts;
  readonly tag: string;
  readonly uniform?: boolean;
}> = ({ params, tag, uniform }) => {
  if (!auditTagTypes.includes(tag as AuditTagType)) {
    return null;
  }

  return (
    <div className={`hstack filter-tag gap-1${uniform ? ` w-100` : ``}`}>
      <span className="ps-1">{tag}</span>
      <div className={`hstack${uniform ? ` ms-auto` : ``}`}>
        <Button
          className={`tag-button ${tag} include`}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          role="link"
          tag={Link}
          to={handleFilter(params, tag, 'include', 'add')}
        >
          <MaterialIcon name="add" small />
        </Button>
        <Button
          className={`tag-button ${tag} exclude`}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          role="link"
          tag={Link}
          to={handleFilter(params, tag, 'exclude', 'add')}
        >
          <MaterialIcon name="remove" small />
        </Button>
      </div>
    </div>
  );
};

interface AuditObjectPageProps {
  readonly entityId: string;
  readonly objectId?: string;
  readonly showBreadcrumbs?: boolean;
}

export const AuditObjectPage: FC<AuditObjectPageProps> = ({
  showBreadcrumbs,
  entityId,
  objectId,
}) => {
  const [pickerModal, setPickerModal] = useState(false);
  const history = useHistory();
  const { search } = useLocation();

  const params = useMemo(() => {
    const query = new URLSearchParams(search);
    return urlSearchParamsToTableOpts(query);
  }, [search]);

  const pageSize = 30;
  const [results, setResults] = useState<AuditTagType[]>([]);
  const [showResults, setShowResults] = useState(false);
  const pickerModalCallback = useCallback(
    (time?: number) => {
      setPickerModal(false);

      if (time) {
        history.push(makeRelativeLogLink(params, { time }, ['reverse']));
      }
    },
    [history, params],
  );

  const fetcher = useFetcher(async (): Promise<LogResult> => {
    return await api.getAuditLogs({
      entityId,
      type: params.type,
      objectId,
      offset: params.offset,
      pageSize,
      reverse: params.reverse,
      time: params.time,
      include: params.include,
      exclude: params.exclude,
    });
  }, [
    entityId,
    params.type,
    params.offset,
    params.reverse,
    params.time,
    params.include,
    params.exclude,
    objectId,
  ]);

  const searchCallback = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    const filterOptions = auditTagTypes.filter((option) => {
      if (option.toLowerCase().includes(query.toLowerCase())) {
        return option;
      }

      return '';
    });

    setResults(filterOptions);
  }, []);

  const detailResult = fetcher.data?.results.find((t) => t.uniqueId === params.view);

  if (!fetcher.data) {
    return <Fetcher result={fetcher} />;
  }

  return (
    <>
      {pickerModal && <TimePickerModal callback={pickerModalCallback} />}
      <Col>
        <Row>
          {detailResult && <LogDetailModal entry={detailResult} params={params} />}
          {showBreadcrumbs && (
            <Col>
              <LogsBreadcrumbs
                entity={fetcher.data.entity}
                object={objectId ? { name: fetcher.data.object, id: objectId } : undefined}
              />
            </Col>
          )}
        </Row>
        <Col>
          <Row style={{ marginLeft: '10px' }}>
            <Col>
              <Row className="mb-1">
                <Input
                  className="search-bar"
                  maxLength={30}
                  onBlur={(e) => {
                    setShowResults(false);
                    e.target.value = '';
                  }}
                  onChange={searchCallback}
                  onFocus={(e) => {
                    setShowResults(true);
                    searchCallback(e);
                  }}
                  placeholder="Search All Tags..."
                  style={{ maxWidth: '265px' }}
                />
              </Row>
              {(params.include.length > 0 || params.exclude.length > 0) && (
                <Row className="shrink-item mb-1">
                  <TagsList direction="include" params={params} tags={params.include} />
                  <TagsList direction="exclude" params={params} tags={params.exclude} />
                </Row>
              )}
              {params.type && (
                <Row className="shrink-item">
                  <TypeFilter params={params} />
                </Row>
              )}
            </Col>
          </Row>
          {showResults && results.length > 0 && (
            <div className="vstack search-results gap-1 mt-1">
              {results.map((option) => {
                return <LogTag key={option} params={params} tag={option} uniform />;
              })}
            </div>
          )}
        </Col>

        <Container>
          {fetcher.data.results.map((entry) => (
            <LogRow entry={entry} key={entry.uniqueId} params={params} />
          ))}
        </Container>
        <LogPagination logs={fetcher.data} params={params} setPickerModal={setPickerModal} />
      </Col>
    </>
  );
};

interface LogPaginationProps {
  readonly logs: LogResult;
  readonly params: TableQueryOpts;
  readonly setPickerModal: (enable: boolean) => void;
}

const LogPagination: FC<LogPaginationProps> = ({ logs, params, setPickerModal }) => {
  const { ts } = useTranslation();
  return (
    <Pagination>
      <PaginationItem disabled={!logs.prevPage}>
        <PaginationLink
          first
          tag={Link}
          to={makeRelativeLogLink(params, {}, ['reverse', 'time'])}
        />
      </PaginationItem>
      <PaginationItem disabled={!logs.prevPage} title={new Date(logs.prevPage!).toDateString()}>
        <PaginationLink
          previous
          tag={Link}
          to={makeRelativeLogLink(params, { time: logs.prevPage, reverse: true })}
        />
      </PaginationItem>
      <PaginationItem disabled={!logs.results[0]}>
        <PaginationLink
          href="#"
          id="auditSetTimeModal"
          onClick={(e) => {
            e.preventDefault();
            setPickerModal(true);
          }}
        >
          {logs.results[0] ? (
            <DateT
              options={{ weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' }}
              value={logs.results[0].createdAt}
            />
          ) : (
            <>({ts('empty')})</>
          )}
        </PaginationLink>
      </PaginationItem>
      <PaginationItem
        disabled={!logs.nextPage}
        id="auditNextPage"
        title={new Date(logs.nextPage!).toDateString()}
      >
        <PaginationLink
          next
          tag={Link}
          to={makeRelativeLogLink(params, { time: logs.nextPage }, ['reverse'])}
        />
      </PaginationItem>
      <PaginationItem disabled={!logs.nextPage}>
        <PaginationLink
          last
          tag={Link}
          to={makeRelativeLogLink(params, { reverse: true }, ['time'])}
        />
      </PaginationItem>
    </Pagination>
  );
};

interface LogRowProps {
  readonly entry: LogEntry;
  readonly params: TableQueryOpts;
}

const LogRow: FC<LogRowProps> = ({ entry, params }) => {
  const { highlight } = params;
  const [details, setDetails] = useToggle(false);
  const isSupportEntry = entry.type.startsWith('support-');
  const styles: CSSProperties = {
    border: highlight === entry.uniqueId ? '2px solid black' : undefined,
    backgroundColor: isSupportEntry ? '#ffefef' : undefined,
    display: 'inline-flex',
  };

  const convention = useConvention();
  const { conventions } = useConfig();
  const shortName = conventions.find((t) => t.id === entry.conventionId)?.shortName;

  return (
    <div className="log-row pt-2 w-100" id={entry.uniqueId} style={styles}>
      <Row className="log-container m-0 flex-nowrap" style={{ paddingLeft: '10px' }}>
        <div className="p-0" style={{ width: '22px', marginLeft: '-18px' }}>
          <MaterialIcon
            className="expand-details"
            id={`${entry.uniqueId}.expand-details`}
            name={details ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
            onClick={setDetails}
            style={{
              marginTop: '-2px',
              padding: '3px',
              zIndex: '10',
              position: 'relative',
            }}
          />
        </div>
        <Col>
          <Row className="log-row-body">
            <Col>
              {entry.conventionId !== convention.id && shortName && <span>{shortName}</span>}
              <span style={{ fontSize: '12px' }}>
                <DateTime value={entry.createdAt} />
              </span>
            </Col>
          </Row>
          <Row className="m-0 shrink-item" style={{ minWidth: '260px', marginLeft: '8px' }}>
            {isSupportEntry && <MaterialIcon className="me-2" name="support_agent" small />}
            {entry.message ? (
              <RichMarkdownIt source={entry.message} />
            ) : (
              <p className="my-0">
                <Link to={`/housekeeping/auditlog/${entry.entityId}/${entry.objectId}`}>
                  {entry.entityId}/{entry.objectId}
                </Link>{' '}
                - {entry.type} - {JSON.stringify(entry.meta)}
              </p>
            )}
          </Row>
          <Row className="pb-1">{details && <LogDetails entry={entry} params={params} />}</Row>
        </Col>
      </Row>
    </div>
  );
};

const LogsBreadcrumbs: FC<{
  readonly entity?: string;
  readonly object?: { name: string; id: string };
}> = ({ entity, object }) => {
  const items: BreadcrumbItemProps[] = [];

  items.push({ active: !entity, text: 'Audit Logs', url: '/housekeeping/auditlog' });

  if (entity) {
    items.push({
      active: !object,
      text: entity,
      url: `/housekeeping/auditlog/${entity}`,
    });

    if (object) {
      items.push({
        active: true,
        text: object.name,
        url: `/housekeeping/auditlog/${entity}/${object.id}`,
      });
    }
  }

  return <Breadcrumb items={items} />;
};
